import { motion } from 'framer-motion'
import { Img } from 'openimg/react'
import { useState } from 'react'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { cn } from '#app/utils/misc.tsx'

interface Sculpture {
	id: string
	title: string
	imageUrl: string | null
}

interface HomepageImageSelectorProps {
	currentImage: Sculpture | null
	allSculptures: Sculpture[]
	positionName: string
	onSelect: (sculptureId: string) => void
	onCancel: () => void
}

export function HomepageImageSelector({
	currentImage,
	allSculptures,
	positionName,
	onSelect,
	onCancel,
}: HomepageImageSelectorProps) {
	const [selectedSculpture, setSelectedSculpture] = useState<Sculpture | null>(
		currentImage,
	)
	const [searchQuery, setSearchQuery] = useState('')

	// Debug log
	console.log('HomepageImageSelector rendered:', {
		currentImage,
		allSculptures: allSculptures.length,
		positionName,
	})

	// Filter sculptures based on search
	const filteredSculptures = allSculptures.filter((sculpture) =>
		sculpture.title.toLowerCase().includes(searchQuery.toLowerCase()),
	)

	const handleSelect = (sculpture: Sculpture) => {
		setSelectedSculpture(sculpture)
	}

	const handleSave = () => {
		if (selectedSculpture && selectedSculpture.id !== currentImage?.id) {
			onSelect(selectedSculpture.id)
		}
	}

	const isChanged = selectedSculpture?.id !== currentImage?.id

	return (
		<div className="space-y-4">
			{/* Current vs Selected display */}
			<div className={cn('grid gap-4', 'grid-cols-1 lg:grid-cols-2')}>
				{/* Current Image */}
				<div className="space-y-2">
					<h4 className="text-muted-foreground text-sm font-medium">
						Huidig beeld ({positionName})
					</h4>
					<div className="relative aspect-square overflow-hidden rounded-lg bg-gray-100">
						{currentImage?.imageUrl ? (
							<Img
								src={currentImage.imageUrl}
								alt={currentImage.title}
								width={512}
								height={512}
								fit="cover"
								className="h-full w-full object-cover"
							/>
						) : (
							<div className="flex h-full items-center justify-center">
								<span className="text-4xl">🎨</span>
							</div>
						)}
						{currentImage && (
							<div className="absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/80 to-transparent p-3">
								<p className="text-sm font-medium text-white">
									{currentImage.title}
								</p>
							</div>
						)}
					</div>
				</div>

				{/* Selected Image (only show on desktop when different) */}
				{isChanged && (
					<div className="hidden space-y-2 lg:block">
						<h4 className="text-muted-foreground text-sm font-medium">
							Geselecteerd beeld
						</h4>
						<motion.div
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							className="ring-primary relative aspect-square overflow-hidden rounded-lg bg-gray-100 ring-2"
						>
							{selectedSculpture?.imageUrl ? (
								<Img
									src={selectedSculpture.imageUrl}
									alt={selectedSculpture.title}
									width={512}
									height={512}
									fit="cover"
									className="h-full w-full object-cover"
								/>
							) : (
								<div className="flex h-full items-center justify-center">
									<span className="text-4xl">🎨</span>
								</div>
							)}
							{selectedSculpture && (
								<div className="absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/80 to-transparent p-3">
									<p className="text-sm font-medium text-white">
										{selectedSculpture.title}
									</p>
								</div>
							)}
						</motion.div>
					</div>
				)}
			</div>

			{/* Search input */}
			<div className="relative">
				<Icon
					name="magnifying-glass"
					className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
				/>
				<Input
					type="search"
					placeholder="Zoek een beeldhouwwerk..."
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					className="pl-10"
				/>
			</div>

			{/* Sculpture grid */}
			{filteredSculptures.length === 0 ? (
				<div className="text-muted-foreground py-8 text-center">
					<p className="mb-2">Geen beeldhouwwerken gevonden</p>
					<p className="text-sm">
						{searchQuery
							? `Geen resultaten voor "${searchQuery}"`
							: 'Er zijn nog geen beeldhouwwerken toegevoegd aan je portfolio'}
					</p>
				</div>
			) : (
				<div className="grid max-h-[400px] grid-cols-2 gap-3 overflow-y-auto md:grid-cols-3">
					{filteredSculptures.map((sculpture) => (
						<motion.button
							key={sculpture.id}
							onClick={() => handleSelect(sculpture)}
							className={cn(
								'relative aspect-square overflow-hidden rounded-lg bg-gray-100 transition-all',
								'hover:ring-primary/50 hover:ring-2',
								selectedSculpture?.id === sculpture.id && 'ring-primary ring-2',
							)}
							whileHover={{ scale: 1.02 }}
							whileTap={{ scale: 0.98 }}
						>
							{sculpture.imageUrl ? (
								<Img
									src={sculpture.imageUrl}
									alt={sculpture.title}
									width={256}
									height={256}
									fit="cover"
									className="h-full w-full object-cover"
								/>
							) : (
								<div className="flex h-full items-center justify-center">
									<span className="text-2xl">🎨</span>
								</div>
							)}
							{/* Always show title overlay */}
							<div className="absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/80 to-transparent p-2">
								<p className="line-clamp-2 text-xs font-medium text-white">
									{sculpture.title}
								</p>
							</div>
							{/* Selected indicator */}
							{selectedSculpture?.id === sculpture.id && (
								<div className="bg-primary text-primary-foreground absolute top-2 right-2 rounded-full p-1">
									<Icon name="check" className="h-3 w-3" />
								</div>
							)}
						</motion.button>
					))}
				</div>
			)}

			{/* Actions */}
			<div className="flex flex-col gap-3 border-t pt-4">
				{isChanged && (
					<div className="bg-primary/10 text-primary animate-in fade-in slide-in-from-bottom-2 rounded-lg p-3 text-sm font-medium">
						✓ Je hebt "{selectedSculpture?.title}" geselecteerd. Klik op
						"Opslaan" om deze wijziging door te voeren.
					</div>
				)}
				<div className="flex items-center justify-between">
					<p className="text-muted-foreground text-sm">
						{!isChanged && 'Selecteer een nieuw beeld door erop te klikken'}
					</p>
					<div className="flex gap-2">
						<Button variant="outline" onClick={onCancel}>
							Annuleren
						</Button>
						<Button
							onClick={handleSave}
							disabled={!isChanged}
							className={isChanged ? 'animate-pulse' : ''}
						>
							Opslaan en voeg toe
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}
